import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AppConfig } from '../app.config';
import { HelpersService } from './helpers.service';
import { SessionExpiredComponent } from '../components/modals/session-expired/session-expired.component';

/**
 * Periodically asks the Old API whether the caller's legacy token is still usable
 * (GET /Account/TokenStatus -> 200 valid / 401 expired / 403 missing-or-invalid) and,
 * on expiry, shows a popup letting the user log out or snooze. Mirrors the existing
 * VersionCheckService poll-then-open-NgbModal pattern.
 */
@Injectable({ providedIn: 'root' })
export class SessionCheckService {
  private started = false;
  private isModalOpen = false;
  private snoozeUntil = 0;
  private readonly authDataKey = 'AuthData';
  private readonly intervalMs = 10 * 60 * 1000;

  constructor(
    private http: HttpClient,
    private modalService: NgbModal,
    private appConfig: AppConfig,
    private helpersService: HelpersService,
  ) {}

  // Idempotent so it can be safely called from more than one authed shell.
  public init(): void {
    if (this.started) {
      return;
    }
    this.started = true;
    this.check();
    setInterval(() => this.check(), this.intervalMs);
  }

  private check(): void {
    const raw = localStorage.getItem(this.authDataKey);
    if (!raw) {
      return; // not logged in — nothing to check
    }
    if (this.isModalOpen || Date.now() < this.snoozeUntil) {
      return; // popup already up, or user is snoozing
    }

    let token: string;
    try {
      token = JSON.parse(window.atob(raw)).token;
    } catch {
      return; // unreadable AuthData — leave it to existing flows
    }
    if (!token) {
      return;
    }

    const headers = new HttpHeaders({ Authorization: `bearer ${token}` });
    this.http
      .get(this.appConfig.getEnvironment() + '/Account/TokenStatus', {
        headers,
        observe: 'response',
      })
      .subscribe({
        next: () => {
          /* 200 = valid, nothing to do */
        },
        error: (err: HttpErrorResponse) => {
          if (err.status === 401) {
            this.openPopup(); // expired
          } else if (err.status === 403) {
            //this.helpersService.removeTokenAndLogout(); // missing/invalid — no usable session
          }
          // other statuses / network errors: fail quiet, try again next interval
        },
      });
  }

  private openPopup(): void {
    if (this.isModalOpen) {
      return;
    }
    this.isModalOpen = true;
    const modalRef = this.modalService.open(SessionExpiredComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
      size: 'lg',
      // Reuse the portal's standard modal chrome — the padding and right-aligned
      // footer buttons live under this window class in _ngbootstrapoverride.scss.
      // Same pattern new-jobcard.service.ts uses to open utility modals from a service.
      windowClass: 'jobcard-modal-popup-window',
      backdropClass: 'jobcard-modal-popup-backdrop',
    });
    modalRef.result.then(
      (result) => {
        this.isModalOpen = false;
        if (result === 'logout') {
          this.helpersService.removeTokenAndLogout();
        }
      },
      (dismissed) => {
        this.isModalOpen = false;
        const minutes =
          dismissed && dismissed.snoozeMinutes ? dismissed.snoozeMinutes : 15;
        this.snoozeUntil = Date.now() + minutes * 60 * 1000;
      },
    );
  }
}
